import {useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Switch} from '@/components/ui/switch'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {CifraModal} from '@/components/cifra-modal'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {BloqueTab} from '@/components/cumplimiento/bloque-tab'
import {SinVerificarTab} from '@/components/cumplimiento/sin-verificar-tab'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {HelpIcon} from '@/components/help-icon'
import {ListEmptyState} from '@/components/section-card'
import {PedirSoporteDialog} from '@/components/pedir-soporte-dialog'
import {PorQue} from '@/components/ui/por-que'
import {useCifras} from '@/hooks/empresa/organizacion/use-cifras'
import {
  useConfirmarFila,
  usePedirLoQueFalta,
  usePedirSoporte,
  useCumplimiento,
  useNoCorresponde,
  useRechazarFila,
  useVolverAExigir,
  type BloqueCumplimiento,
  type FilaCumplimiento,
  type Hoja,
} from '@/hooks/empresa/cumplimiento/use-cumplimiento'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** «Sin verificar» va al final: leer lo no establecido antes de lo establecido invierte la pantalla. */
const SIN_VERIFICAR = 'sin-verificar'

const SUB_PESTANA = 'px-2.5 py-2 text-[10px]'

/** Los dos números siempre juntos: con el interruptor el segundo se dice que no cuenta, no desaparece. */
function Numeros({
  numeros,
  sinSugerido,
  testId,
}: {
  numeros: {confirmado: number; con_sugerido: number; total: number}
  sinSugerido: boolean
  testId: string
}) {
  const {t} = useTranslation()
  return (
    <span className="font-mono text-xs" data-testid={testId}>
      {t('cumplimiento.numeros.confirmado', {n: numeros.confirmado, total: numeros.total})}
      {sinSugerido
        ? ` · ${t('cumplimiento.numeros.sugeridoNoCuenta')}`
        : ` · ${t('cumplimiento.numeros.conSugerido', {n: numeros.con_sugerido, total: numeros.total})}`}
    </span>
  )
}

/** Dos palabras (U21). El chip del hueco lo acompaña siempre, con el mismo `huecos`: así el verde
 * no afirma solo (E1 D-T). */
function Sello({hoja}: {hoja: Hoja}) {
  const {t} = useTranslation()
  // Las líneas de salud retenidas viajan colapsadas: el número es el de chequeos, no el de renglones.
  const retenidas = hoja.salud_reducida ?? 0
  const huecos = hoja.no_verificables.length + Math.max(retenidas - 1, 0)
  if (hoja.sin_cargo) {
    return (
      <>
        <Tag tone="neutral" className="w-fit" data-testid="cumplimiento-veredicto">
          {t('cumplimiento.franja.sinCargoChip')}
        </Tag>
        <HelpIcon label={t('cumplimiento.franja.sinCargoAyudaLabel')} testId="cumplimiento-sin-cargo-ayuda">
          {t('cumplimiento.franja.sinCargoAyuda')}
        </HelpIcon>
      </>
    )
  }
  if (!hoja.veredicto.habilitada) {
    return (
      <Tag tone="red" className="w-fit" data-testid="cumplimiento-veredicto">
        {t('cumplimiento.franja.noHabilitada')}
      </Tag>
    )
  }
  return (
    <>
      <Tag tone="green" className="w-fit" data-testid="cumplimiento-veredicto">
        {t('cumplimiento.franja.habilitada')}
      </Tag>
      {huecos > 0 && <SinVerificar huecos={huecos} />}
    </>
  )
}

/** El detalle va al `title` y a un `sr-only`: el `title` solo no lo abre un teclado ni un teléfono. */
function SinVerificar({huecos}: {huecos: number}) {
  const {t} = useTranslation()
  const detalle = t('cumplimiento.franja.habilitadaConHuecos', {count: huecos})
  return (
    <>
      <Tag tone="amber" className="w-fit" title={detalle} data-testid="cumplimiento-sin-verificar">
        {t('cumplimiento.franja.sinVerificar', {count: huecos})}
      </Tag>
      <span className="sr-only" data-testid="cumplimiento-sin-verificar-detalle">
        {detalle}
      </span>
    </>
  )
}

function porResolver(bloque: BloqueCumplimiento): {total: number; rojos: number} {
  const rojos = bloque.filas.filter((fila) => fila.color === 'rojo').length
  const amarillos = bloque.filas.filter((fila) => fila.color === 'amarillo').length
  return {total: rojos + amarillos, rojos}
}

/** En cero no existe: un «0» pintado parece un pendiente. */
function ChipPestana({total, rojos, testId}: {total: number; rojos: number; testId: string}) {
  if (total === 0) return null
  return (
    <Tag tone={rojos > 0 ? 'red' : 'amber'} className="ml-1.5" data-testid={testId}>
      {total}
    </Tag>
  )
}

/** Aquí el cero sí se pinta: que no haya rojos es la mitad del veredicto. */
function Desglose({hoja}: {hoja: Hoja}) {
  const {t} = useTranslation()
  const d = hoja.desglose
  const fuera = d.azules + d.grises
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="cumplimiento-desglose">
      <Tag tone="red">{t('cumplimiento.desglose.rojos', {count: d.rojos})}</Tag>
      <Tag tone="amber">{t('cumplimiento.desglose.amarillos', {count: d.amarillos})}</Tag>
      <Tag tone="green">{t('cumplimiento.desglose.verdes', {count: d.verdes})}</Tag>
      <Tag tone="neutral">{t('cumplimiento.desglose.fuera', {count: fuera})}</Tag>
      {hoja.veredicto.rojos_no_deshabilitantes > 0 && (
        <Tag tone="neutral" data-testid="cumplimiento-rojos-no-deshabilitantes">
          {t('cumplimiento.desglose.noDeshabilitan', {count: hoja.veredicto.rojos_no_deshabilitantes})}
        </Tag>
      )}
    </div>
  )
}

/** Un solo primario; la procedencia del veredicto (P-E10-1) se pide en «¿Con qué criterio?» (U5/T7). */
function Cabecera({
  hoja,
  sinSugerido,
  onSinSugerido,
  perfilHref,
  rejillaHref,
  puedeOperar,
  onPedirTodo,
}: {
  hoja: Hoja
  sinSugerido: boolean
  onSinSugerido: (valor: boolean) => void
  perfilHref: string | null
  rejillaHref: string | null
  puedeOperar: boolean
  onPedirTodo: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="surface sticky top-0 z-20 space-y-2 rounded-md p-4" data-testid="cumplimiento-cabecera">
      <div className="flex flex-wrap items-center gap-2">
        <Sello hoja={hoja} />
        <PorQue
          label={t('cumplimiento.criterio.label')}
          testId="cumplimiento-criterio-boton"
          contenidoTestId="cumplimiento-criterio">
          <p>
            {t(`cumplimiento.criterio.${hoja.veredicto.criterio_origen}`, {
              fecha: hoja.veredicto.criterio_fijada_en ? formatDateOnly(hoja.veredicto.criterio_fijada_en) : '',
              nombre: hoja.veredicto.criterio_fijada_por ?? '',
            })}{' '}
            {t('cumplimiento.franja.noDecide')}
          </p>
        </PorQue>
        <span className="text-sm font-medium">{hoja.nombre}</span>
        <span className="font-mono text-xs text-muted-foreground">{hoja.cedula}</span>
        {/* Vacía sin cargo: el chip «Sin cargo» ya lo dice; el nodo queda por su testid. */}
        <span className="text-sm" data-testid="cumplimiento-cargo">
          {hoja.sin_cargo
            ? ''
            : t('cumplimiento.franja.cargo', {cargos: hoja.cargos.map((cargo) => cargo.nombre).join(', ')})}
        </span>
      </div>
      {hoja.retirado && (
        <p className="notice-amber rounded-md px-3 py-2 text-xs" data-testid="cumplimiento-retirado">
          {t('cumplimiento.franja.retirado')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Numeros numeros={hoja.numeros} sinSugerido={sinSugerido} testId="cumplimiento-numeros" />
        <Desglose hoja={hoja} />
        <Label className="flex items-center gap-2 text-xs">
          <Switch
            checked={sinSugerido}
            onCheckedChange={onSinSugerido}
            data-testid="cumplimiento-sin-sugerido"
            aria-label={t('cumplimiento.numeros.interruptor')}
          />
          {t('cumplimiento.numeros.interruptor')}
        </Label>
        <Button
          size="sm"
          disabled={!puedeOperar || hoja.numeros.total === 0}
          onClick={onPedirTodo}
          data-testid="cumplimiento-pedir-lo-que-falta">
          {t('cumplimiento.acciones.pedirLoQueFalta')}
        </Button>
        {perfilHref && (
          <Link className="text-xs underline" to={perfilHref} data-testid="cumplimiento-ver-perfil">
            {t('cumplimiento.acciones.verPerfil')}
          </Link>
        )}
        {/* Único punto de entrada a la rejilla en la aplicación. */}
        {rejillaHref && (
          <Link className="text-xs underline" to={rejillaHref} data-testid="cumplimiento-ver-rejilla">
            {t('cumplimiento.acciones.verRejilla')}
          </Link>
        )}
      </div>
    </div>
  )
}

function DescartarDialog({
  fila,
  onCerrar,
  onConfirmar,
  guardando,
  error,
}: {
  fila: FilaCumplimiento | null
  onCerrar: () => void
  onConfirmar: (motivo: string) => void
  guardando: boolean
  error?: string
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')
  const [hallazgo, setHallazgo] = useState<string | undefined>()
  if (!fila) return null
  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cumplimiento.descartar.titulo')}</DialogTitle>
          <DialogDescription>{t('cumplimiento.descartar.descripcion')}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(evento) => {
            evento.preventDefault()
            const problema = validateRequired(motivo.trim())
            setHallazgo(problema ?? undefined)
            if (!problema) onConfirmar(motivo.trim())
          }}>
          <div className="space-y-1.5">
            <Label htmlFor="cumplimiento-motivo">{t('cumplimiento.descartar.motivo')}</Label>
            <Textarea
              maxLength={tope('NOTA')}
              id="cumplimiento-motivo"
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              onBlur={() => setHallazgo(validateRequired(motivo.trim()) ?? undefined)}
              data-testid="cumplimiento-motivo"
            />
            <FieldError error={hallazgo} />
          </div>
          <FormDialogFooter
            onCancel={onCerrar}
            submitLabel={t('cumplimiento.descartar.confirmar')}
            pending={guardando}
            error={error ?? null}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** D10, R2. Rechazar pide motivo, no borra el archivo y la fila sigue contando. El error de los dos
 * actos (p. ej. un 409 concurrente) se pinta arriba. */
function RevisarDialog({
  fila,
  onCerrar,
  onConfirmar,
  onRechazar,
  guardando,
  error,
}: {
  fila: FilaCumplimiento | null
  onCerrar: () => void
  onConfirmar: () => void
  onRechazar: (motivo: string) => void
  guardando: boolean
  error?: string
}) {
  const {t} = useTranslation()
  const [rechazando, setRechazando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [hallazgo, setHallazgo] = useState<string | undefined>()
  if (!fila) return null
  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cumplimiento.revisar.titulo', {nombre: fila.nombre})}</DialogTitle>
          <DialogDescription>{t('cumplimiento.revisar.descripcion')}</DialogDescription>
        </DialogHeader>
        {rechazando ? (
          <form
            noValidate
            className="space-y-3"
            onSubmit={(evento) => {
              evento.preventDefault()
              const problema = validateRequired(motivo.trim())
              setHallazgo(problema ?? undefined)
              if (!problema) onRechazar(motivo.trim())
            }}>
            <div className="space-y-1.5">
              <Label htmlFor="cumplimiento-rechazo">{t('cumplimiento.revisar.motivo')}</Label>
              <Textarea
                maxLength={tope('NOTA')}
                id="cumplimiento-rechazo"
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                onBlur={() => setHallazgo(validateRequired(motivo.trim()) ?? undefined)}
                data-testid="cumplimiento-rechazo-motivo"
              />
              <FieldError error={hallazgo} />
            </div>
            <FormDialogFooter
              onCancel={onCerrar}
              submitLabel={t('cumplimiento.revisar.rechazar')}
              pending={guardando}
              error={error ?? null}
            />
          </form>
        ) : (
          <div className="space-y-2">
            {error && (
              <p className="text-sm text-destructive" data-testid="cumplimiento-revisar-error">
                {error}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={onConfirmar} disabled={guardando} data-testid="cumplimiento-revisar-confirmar">
                {t('cumplimiento.boton.confirmar')}
              </Button>
              <Button variant="outline" onClick={() => setRechazando(true)} data-testid="cumplimiento-revisar-rechazar">
                {t('cumplimiento.revisar.rechazar')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** E10-H07. Las dos puertas van por separado: cuatro actos son `require_firmante` y «Pedirlo» es
 * `require_operador`; una sola bandera daría 403 o escondería el pedido. */
export function EmployeeCumplimientoSection({
  businessId,
  employeeId,
  puedeOperar,
  puedeFirmar,
  puedeCustodiar,
  razonBloqueo,
}: {
  businessId: string
  employeeId: string
  puedeOperar: boolean
  puedeFirmar: boolean
  puedeCustodiar: boolean
  razonBloqueo?: string | null
}) {
  const {t} = useTranslation()
  const hoja = useCumplimiento(businessId, employeeId)
  const [sinSugerido, setSinSugerido] = useState(false)
  const [descartando, setDescartando] = useState<FilaCumplimiento | null>(null)
  const [pidiendo, setPidiendo] = useState<FilaCumplimiento | null>(null)
  const [pidiendoTodo, setPidiendoTodo] = useState(false)
  const [cifraAbierta, setCifraAbierta] = useState<string | null>(null)
  const [revisando, setRevisando] = useState<FilaCumplimiento | null>(null)
  const [reexigiendo, setReexigiendo] = useState<FilaCumplimiento | null>(null)
  const [acuse, setAcuse] = useState<number | null>(null)
  const [pestana, setPestana] = useState<string | null>(null)
  const [yendoAChoques, setYendoAChoques] = useState(false)
  const confirmar = useConfirmarFila(businessId, employeeId)
  const noCorresponde = useNoCorresponde(businessId, employeeId)
  const volverAExigir = useVolverAExigir(businessId, employeeId)
  const pedir = usePedirSoporte(businessId, employeeId)
  const pedirTodo = usePedirLoQueFalta(businessId, employeeId)
  const rechazar = useRechazarFila(businessId, employeeId)
  const cifras = useCifras(businessId)
  const navigate = useNavigate()

  // Radix desmonta la pestaña inactiva: el desplazamiento espera a que «Sin verificar» se monte.
  useEffect(() => {
    if (!yendoAChoques) return
    document.querySelector('[data-testid="choques-section"]')?.scrollIntoView({behavior: 'smooth', block: 'center'})
    setYendoAChoques(false)
  }, [yendoAChoques])

  if (hoja.isLoading) return <Skeleton className="h-40 w-full" />
  if (!hoja.data) return <ListEmptyState title={t('cumplimiento.errorCarga')} />

  // Una persona retirada conserva su hoja pero no se le pide nada.
  const activa = !hoja.data.retirado
  const operar = puedeOperar && activa
  const firmar = puedeFirmar && activa
  const custodiar = puedeCustodiar && activa

  /** La fila sin estado fija su cifra con el mismo modal y `PUT /cifras/{cifra_key}` de Cifras. */
  function accion(fila: FilaCumplimiento) {
    if (fila.estado === null) return setCifraAbierta(fila.cifra_key)
    if (fila.estado === 'no_le_corresponde') return setReexigiendo(fila)
    if (fila.estado === 'sugerido' || fila.estado === 'sugerido_fecha_vencida') return setRevisando(fila)
    // §9 (a), estado 1: el visor es la pestaña «Soportes» de esta misma ficha.
    if (fila.estado === 'cumple') return navigate(`/businesses/${businessId}/empleados/${employeeId}?tab=soportes`)
    if (fila.estado === 'en_curso') return navigate(`/businesses/${businessId}/sgsst/solicitudes`)
    if (fila.estado === 'choque_restriccion') {
      setPestana(SIN_VERIFICAR)
      setYendoAChoques(true)
      return
    }
    return setPidiendo(fila)
  }

  const bloques = hoja.data.bloques
  // Sin rojos abre Exámenes: el único bloque que toda persona tiene, con cargo o sin él.
  const conRojo = bloques.find((bloque) => bloque.filas.some((fila) => fila.color === 'rojo'))
  const abierta = pestana ?? conRojo?.bloque ?? bloques[0]?.bloque ?? SIN_VERIFICAR
  const primerCargo = hoja.data.cargos[0]
  return (
    <div className="space-y-4" data-testid="cumplimiento-hoja">
      <Cabecera
        hoja={hoja.data}
        sinSugerido={sinSugerido}
        onSinSugerido={setSinSugerido}
        perfilHref={primerCargo ? `/businesses/${businessId}/sgsst/perfiles-cargo/${primerCargo.position_id}` : null}
        rejillaHref={
          primerCargo ? `/businesses/${businessId}/sgsst/positions/${primerCargo.position_id}/rejilla` : null
        }
        puedeOperar={operar}
        onPedirTodo={() => setPidiendoTodo(true)}
      />
      {acuse !== null && (
        <p className="notice-amber rounded-md px-3 py-2 text-sm" data-testid="cumplimiento-acuse">
          {t('cumplimiento.acciones.acuse', {count: acuse})}
        </p>
      )}
      {!firmar && razonBloqueo && (
        <p className="text-xs text-muted-foreground" data-testid="cumplimiento-bloqueo">
          {razonBloqueo}
        </p>
      )}
      {volverAExigir.isError && (
        <p className="text-sm text-destructive" data-testid="cumplimiento-error-seccion">
          {translateApiError(volverAExigir.error)}
        </p>
      )}
      <Tabs value={abierta} onValueChange={setPestana}>
        <TabsList className="flex-wrap gap-x-2 gap-y-1">
          {bloques.map((bloque) => {
            const chip = porResolver(bloque)
            return (
              <TabsTrigger
                key={bloque.bloque}
                value={bloque.bloque}
                className={SUB_PESTANA}
                data-testid={`cumplimiento-tab-${bloque.bloque}`}>
                {t(`cumplimiento.tabs.${bloque.bloque}`)}
                <ChipPestana {...chip} testId={`cumplimiento-chip-${bloque.bloque}`} />
              </TabsTrigger>
            )
          })}
          <TabsTrigger value={SIN_VERIFICAR} className={SUB_PESTANA} data-testid="cumplimiento-tab-sin-verificar">
            {t('cumplimiento.tabs.sinVerificar')}
            {hoja.data.no_verificables.length > 0 && (
              <Tag tone="neutral" className="ml-1.5" data-testid="cumplimiento-chip-sin-verificar">
                {hoja.data.no_verificables.length}
              </Tag>
            )}
          </TabsTrigger>
        </TabsList>
        {bloques.map((bloque) => (
          <TabsContent key={bloque.bloque} value={bloque.bloque} className="pt-4">
            <BloqueTab
              bloque={bloque}
              sinPerfil={hoja.data.sin_cargo}
              puedeOperar={operar}
              puedeFirmar={firmar}
              puedeCustodiar={custodiar}
              onAccion={accion}
              onDescartar={setDescartando}
            />
          </TabsContent>
        ))}
        <TabsContent value={SIN_VERIFICAR} className="pt-4">
          <SinVerificarTab
            businessId={businessId}
            employeeId={employeeId}
            lineas={hoja.data.no_verificables}
            canEdit={firmar}
            onFijarCifra={setCifraAbierta}
          />
        </TabsContent>
      </Tabs>
      <RevisarDialog
        key={`revisar-${revisando?.requisito_id ?? 'ninguna'}`}
        fila={revisando}
        onCerrar={() => setRevisando(null)}
        guardando={confirmar.isPending || rechazar.isPending}
        error={
          confirmar.isError
            ? translateApiError(confirmar.error)
            : rechazar.isError
              ? translateApiError(rechazar.error)
              : undefined
        }
        onConfirmar={() =>
          confirmar.mutate({requisitoId: revisando!.requisito_id}, {onSuccess: () => setRevisando(null)})
        }
        onRechazar={(motivo) =>
          rechazar.mutate(
            {requisitoId: revisando!.requisito_id, cuerpo: {motivo}},
            {onSuccess: () => setRevisando(null)}
          )
        }
      />
      <PedirSoporteDialog
        key={`pedir-${pidiendo?.requisito_id ?? 'ninguna'}`}
        abierto={!!pidiendo}
        titulo={t('cumplimiento.pedir.titulo', {nombre: pidiendo?.nombre ?? ''})}
        notaPorDefecto={pidiendo?.nombre ?? ''}
        testId="cumplimiento"
        onCerrar={() => setPidiendo(null)}
        guardando={pedir.isPending}
        error={pedir.isError ? translateApiError(pedir.error) : undefined}
        onConfirmar={(plazo, nota) =>
          pedir.mutate(
            {requisitoId: pidiendo!.requisito_id, cuerpo: {plazo, nota}},
            {onSuccess: () => setPidiendo(null)}
          )
        }
      />
      <PedirSoporteDialog
        key={`pedir-todo-${pidiendoTodo}`}
        abierto={pidiendoTodo}
        titulo={t('cumplimiento.acciones.pedirLoQueFalta')}
        notaPorDefecto={t('cumplimiento.acciones.pedirLoQueFalta')}
        testId="cumplimiento-todo"
        onCerrar={() => setPidiendoTodo(false)}
        guardando={pedirTodo.isPending}
        error={pedirTodo.isError ? translateApiError(pedirTodo.error) : undefined}
        onConfirmar={(plazo, nota) =>
          pedirTodo.mutate(
            {plazo, nota},
            {
              onSuccess: (data) => {
                setPidiendoTodo(false)
                setAcuse(data.pedidos)
              },
            }
          )
        }
      />
      <CifraModal
        open={!!cifraAbierta}
        onOpenChange={() => setCifraAbierta(null)}
        businessId={businessId}
        cifra={cifras.data?.cifras.find((fila) => fila.cifra_key === cifraAbierta) ?? null}
      />
      <DescartarDialog
        key={`descartar-${descartando?.requisito_id ?? 'ninguna'}`}
        fila={descartando}
        onCerrar={() => setDescartando(null)}
        guardando={noCorresponde.isPending}
        error={noCorresponde.isError ? translateApiError(noCorresponde.error) : undefined}
        onConfirmar={(motivo) =>
          noCorresponde.mutate(
            {requisitoId: descartando!.requisito_id, cuerpo: {motivo}},
            {onSuccess: () => setDescartando(null)}
          )
        }
      />
      {/* Se confirma: disparado sin diálogo, dos clics mandaban dos POST y el segundo daba 409. */}
      <ConfirmDialog
        open={!!reexigiendo}
        onOpenChange={() => setReexigiendo(null)}
        title={t('cumplimiento.reexigir.titulo')}
        description={t('cumplimiento.reexigir.descripcion')}
        porque={{
          label: t('cumplimiento.reexigir.porqueLabel'),
          testId: 'cumplimiento-reexigir-porque',
          contenidoTestId: 'cumplimiento-reexigir-porque-contenido',
          contenido: <p>{t('cumplimiento.reexigir.porqueBody')}</p>,
        }}
        confirmLabel={t('cumplimiento.boton.volverAExigir')}
        cancelLabel={t('common.cancel')}
        pending={volverAExigir.isPending}
        onConfirm={() =>
          volverAExigir.mutate({requisitoId: reexigiendo!.requisito_id}, {onSuccess: () => setReexigiendo(null)})
        }
      />
    </div>
  )
}
